import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useActionsOff } from '../../auth/useRolePreview';
import { useSearchParams } from 'react-router-dom';
import {
  useAckAlert,
  useAlertMutes,
  useAlerts,
  useBulkResolveAlerts,
  useCreateAlertMute,
  useDeleteAlertMute,
  useResolveAlert,
  useRobots,
  type AlertMuteOut,
  type BulkResolveResult,
} from '../../api/hooks';
import { adaptAlert, type AlertViewModel } from '../../api/adapters';
import {
  DATA_TABLE_PHONE_QUERY,
  DataTable,
  type DataTableColumn,
} from '../../design-system/components/DataTable';
import {
  RowMenu,
  ScreenTabs,
  ScreenTitleRow,
  SummaryLine,
  TableToolbar,
} from '../../design-system/components/ScreenParts';
import { StatusChip } from '../../design-system/components/StatusChip';
import { EmptyState } from '../../design-system/components/EmptyState';
import { AlertBanner } from '../../design-system/components/AlertBanner';
import { SkeletonLoader } from '../../design-system/components/SkeletonLoader';
import { FormDialog } from '../../design-system/components/FormDialog';
import { useActiveSite } from '../../state/useActiveSite';
import { getRuntimeConfig } from '../../config/runtime-config';
import { formatDateTime } from '../../utils/date-format';
import { actorTitle, formatActor } from '../Audit/format-actor';
import { audienceFor } from '../../auth/roles';
import { useAuth } from '../../auth/useAuth';
import { useAlertWords } from '../../state/alert-notifications/useAlertWords';
import type { AlertWords } from '../../state/alert-notifications/alert-words';
import { navigationTargetFor } from '../../state/alert-deep-link';
import { alertPlaceFocus } from '../../state/show-where';
import { openReportProblem } from '../../state/report-problem';
import { isMuted, subscribeMute } from '../../state/alert-audio';
import { useMediaQuery } from '../../state/useMediaQuery';
import { ShowWhereLink } from '../../shell/ShowWhereLink';
import { useDoors } from '../../api/hooks/useDoors';
import { useLifts } from '../../api/hooks/useLifts';
import { useCanSeeSimulated } from '../../auth/useCanSeeSimulated';
import { isSimulatedFleet } from '../../state/simulated-fleet';
import { naturalKey } from '../Devices/devices-rows';
import {
  alertAgeWords,
  alertMatchesKind,
  alertOpenedMs,
  alertOpensExpanded,
  alertOpensExpandedOnPhone,
  alertOrderKey,
  alertRuleWords,
  alertsUnderBox,
  alertSummary,
  reportPrefillFor,
  SEVERITY_WORD,
  splitAlertWords,
  UNDER_BOX_WORDS,
  type AlertKindFilter,
} from './alert-rows';
import { useAlertNotificationsStable } from '../../state/useAlertNotifications';
import { dayClockText } from '../../state/attention/words';
import { useMinuteTick } from '../../state/clock';
import { resolveSiteTimeZone } from '../Tasks/site-timezone';
import './AlertsScreen.css';
import { NoSiteNotice } from '../../shell/NoSiteNotice';
import { HelpScreenNote } from '../../help';
import { MUTE_DIALOG_HINT, offersMutes } from './mute-policy';
import { alertWhereText } from './alert-where';
import { useAlertWhereFacts } from './useAlertWhereFacts';
import { useAlertLinkOpens } from './useAlertLinkOpens';

function severityKind(sev: string): 'error' | 'warning' | 'idle' {
  if (sev === 'critical') return 'error';
  if (sev === 'warning') return 'warning';
  return 'idle';
}

/**
 * The source kinds that are NOT device-shaped, i.e. the ones a
 * per-device mute cannot meaningfully address.
 *
 * A DENYLIST, deliberately, and not the hand-written allowlist this
 * used to be. That list read `['robot','door','lift','gateway']` and
 * so silently omitted `door_adapter` — the one rule in
 * `alerts_engine.py` that NEVER clears itself (a door adapter left in
 * shadow/disarmed keeps publishing the disarmed condition forever,
 * because nothing re-arms it automatically by design). That is
 * precisely the alert an operator most needs to mute during
 * commissioning, and "Mute device…" was the one row it was never
 * offered on. An allowlist fails closed on exactly the kind nobody
 * remembered to add; a denylist fails open on a kind that is at worst
 * useless, so the failure mode is the survivable one.
 *
 * The engine's full `source_kind` vocabulary today is `robot`, `door`,
 * `door_adapter`, `lift`, `gateway` (all device-shaped) plus `task`
 * (about a piece of work, not a device) and `site` (the fire-mode row,
 * which `alerts_engine.py::_NEVER_MUTED` and `api/alerts.py::
 * _guard_fire_alert` refuse to mute or resolve anyway). The backend
 * does not validate `source_kind` against an enum — `AlertMuteIn`
 * bounds it to 1..16 chars and nothing more — so this set is purely
 * the console's judgement about which rows are worth offering.
 */
const NON_DEVICE_SOURCE_KINDS = new Set(['task', 'site']);

/** Whether the mutes API can be pointed at this alert's source. */
function isMutableSourceKind(kind: string): boolean {
  return !NON_DEVICE_SOURCE_KINDS.has(kind);
}

/** Row model: the adapted view plus the raw `sourceKind`/`sourceId`
 *  the mutes API is keyed on. Read straight off the wire row here
 *  rather than adding them to `AlertViewModel` — no other screen
 *  needs them, and parsing them back out of the synthesized
 *  `deepLink` string would be a round-trip through a display field. */
interface AlertRow extends AlertViewModel {
  sourceKind?: string;
  sourceId?: string;
}

const TTL_CHOICES = [
  { value: '24h', label: '24 hours', ttlMs: 86_400_000 as number | undefined },
  { value: '7d', label: '7 days', ttlMs: 604_800_000 as number | undefined },
  { value: 'forever', label: 'Until unmuted', ttlMs: undefined as number | undefined },
] as const;

type TtlChoice = (typeof TTL_CHOICES)[number]['value'];

/** What a site reader is told when the list cannot be read: no status, no
 *  server text (`useAlerts` throws "Failed to read alerts: HTTP 502" and
 *  the like, which is for ops). */
export const SITE_LOAD_ERROR_TITLE = "Can't load alerts right now";
export const SITE_LOAD_ERROR_TEXT = 'Try again in a minute.';
/** Said under it when rows from the last good read are still on screen. */
export const SITE_LOAD_ERROR_STALE = 'The list below may be out of date.';
/** A failed ack or resolve, for a site reader (who is offered no mute):
 *  the mutation errors carry the alert's id and an HTTP status. */
export const SITE_ACTION_ERROR_TEXT =
  "It didn't go through. Try again, or call Nexsoss if it keeps failing.";

const SEVERITY_SORT = { columnId: 'severity', direction: 'asc' } as const;
const DEVICE_SORT = { columnId: 'device', direction: 'asc' } as const;

type AlertsTab = 'open' | 'muted';

const KIND_FILTERS: readonly { id: AlertKindFilter; label: string }[] = [
  { id: 'all', label: 'All kinds' },
  { id: 'robots', label: 'Robots' },
  { id: 'lifts', label: 'Lifts' },
  { id: 'doors', label: 'Doors' },
];

/** The kinds "Mute a device…" offers. The kinds whose id a person would not
 *  know (a door system, an edge service) are muted from one of their rows. */
const MUTE_PICK_KINDS = [
  { value: 'robot', label: 'Robot' },
  { value: 'lift', label: 'Lift' },
  { value: 'door', label: 'Door' },
] as const;

export function AlertsScreen() {
  const alerts = useAlerts();
  const { site, sites } = useActiveSite();
  // Who reads the rows. `audienceFor`, never a role list of our own: a
  // mixed account reads ops, and `site_supervisor` and `service_operator`
  // read site (amendment A2). A site audience reads every row through the
  // same words the toast and the bell use (`useAlertWords`): hotel words,
  // no codes, the site's clock. Ops read the engine's text, as before.
  const { roles } = useAuth();
  const audience = audienceFor(roles);
  // "View as role" is look-only: the buttons stay drawn as the role
  // would see them and are switched off (the client refuses the write
  // regardless; this only saves a press).
  const actionsOff = useActionsOff();
  // Mutes are ops' (A18): every mute control below is gated on this one
  // value, the dialog included, so a session whose roles drop to a site
  // audience mid-dialog loses the dialog too.
  const canMute = offersMutes(audience);
  const wordsFor = useAlertWords();
  const siteId = site?.id ?? null;
  const clock = useMemo(() => resolveSiteTimeZone(sites, siteId), [sites, siteId]);
  // Names the ack time's day ("yesterday 23:58") and turns it over at
  // midnight; it also re-reads the words below each minute.
  const wallNowMs = useMinuteTick();
  const ack = useAckAlert();
  const resolve = useResolveAlert();
  const bulkResolve = useBulkResolveAlerts();
  const mutes = useAlertMutes();
  const createMute = useCreateAlertMute();
  const deleteMute = useDeleteAlertMute();
  const flags = getRuntimeConfig().featureFlags;
  // Where each row's device is now (`alert-where.ts`), and what a click on
  // a row does: exactly what a click on the bell's item for it does.
  const whereFacts = useAlertWhereFacts();
  const { handleAlertAction } = useAlertNotificationsStable();
  const linkOpens = useAlertLinkOpens();

  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [bulkOutcome, setBulkOutcome] = useState<BulkResolveResult | null>(null);
  const [muteTarget, setMuteTarget] = useState<{ kind: string; id: string } | null>(null);
  const [muteReason, setMuteReason] = useState('');
  const [muteTtl, setMuteTtl] = useState<TtlChoice>('24h');
  const [muteOutcome, setMuteOutcome] = useState<{ device: string; resolved: number } | null>(null);
  const [tab, setTab] = useState<AlertsTab>('open');
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState<AlertKindFilter>('all');
  /** What the person opened or closed; any other row keeps its default. */
  const [boxOverride, setBoxOverride] = useState<ReadonlyMap<string, boolean>>(new Map());
  /** "Mute a device…", where the kind and the device are still to choose. */
  const [mutePicking, setMutePicking] = useState(false);
  const narrow = useMediaQuery(DATA_TABLE_PHONE_QUERY);
  const soundMuted = useSyncExternalStore(subscribeMute, isMuted);

  // The devices "Mute a device…" can name, as their alerts' rows name them.
  const showSimulated = useCanSeeSimulated();
  const robotList = useRobots().data;
  const liftList = useLifts().data;
  const doorList = useDoors().data;
  const pickableDevices = (kind: string): string[] => {
    const names =
      kind === 'robot'
        ? (robotList ?? []).filter((r) => showSimulated || !isSimulatedFleet(r.fleet)).map((r) => r.name)
        : kind === 'lift'
          ? (liftList ?? []).map((l) => l.name)
          : (doorList ?? []).map((d) => d.name);
    return [...new Set(names)].sort((a, b) => (naturalKey(a) < naturalKey(b) ? -1 : 1));
  };

  const rows: AlertRow[] = useMemo(
    () =>
      (alerts.data ?? [])
        .map((raw) => {
          const r = raw as Record<string, unknown>;
          return {
            ...adaptAlert(raw),
            sourceKind: typeof r.sourceKind === 'string' ? r.sourceKind : undefined,
            sourceId: typeof r.sourceId === 'string' ? r.sourceId : undefined,
          };
        }),
    [alerts.data],
  );

  // The rows a silent site box causes, which stay here under it.
  const underBox = useMemo(() => alertsUnderBox(rows), [rows]);
  const boxOf = (a: AlertRow) => {
    const id = underBox.get(a.id);
    return id ? rows.find((r) => r.id === id) : undefined;
  };

  // Re-read on the minute tick too, not only when the rows change: words
  // that name a time's day read the wall clock when they are made, and a
  // poll that returns the same rows keeps the same array, so without the
  // tick "23:58" would stay "23:58" past midnight.
  const wordsById = useMemo(
    () => new Map(rows.map((a) => [a.id, wordsFor(a)] as const)),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- wallNowMs: see above
    [rows, wordsFor, wallNowMs],
  );
  /** The adapter's words for a row; the engine's own text when absent. */
  const wordsOf = (a: AlertRow): AlertWords =>
    wordsById.get(a.id) ?? {
      source: a.source,
      message: a.message,
      translated: false,
      openedAt: null,
      headTime: null,
    };
  /** The row's device and its floor now (`alertWhereText`). Its fallback
   *  subject is what the Source column showed before Where replaced it:
   *  the bell's own subject line for translated words (they carry their
   *  own: the robot, the lift, "Site box"), the row's own source else. */
  const whereOf = (a: AlertRow): string => {
    const words = wordsOf(a);
    return alertWhereText(a, audience, words.translated ? words.source : a.source, whereFacts);
  };

  /**
   * `?alert=<id>` — where a notification lands.
   *
   * A chat card or an email names ONE alert, and until now the best it
   * could offer was the alerts list, leaving the reader to find the row
   * again from a message they had already read. This points at it.
   *
   * IT ONLY POINTS. No selecting, no sorting, no filtering: a link
   * someone followed must not quietly change what the screen is
   * showing, or two operators reading the same board disagree about
   * what is on it. If the id is not in the list the row is simply not
   * there to highlight — which is the common case, because the list is
   * the OPEN alerts and a notification often outlives its alert. That
   * case is said out loud below rather than looking like a dead link.
   */
  const [searchParams] = useSearchParams();
  const focusAlertId = searchParams.get('alert');
  const focusFound = Boolean(focusAlertId && rows.some((a) => a.id === focusAlertId));
  const tableRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!focusAlertId || !focusFound) return;
    // After the rows are painted, and only once per id: an alert list
    // polls, and re-scrolling on every refresh would fight an operator
    // who has scrolled away.
    const row = tableRef.current?.querySelector('[data-highlighted="true"]');
    // Guarded, because an exception thrown from an effect is not
    // contained: React unmounts the tree, so a browser without
    // `scrollIntoView` would trade "the row was not scrolled to" for a
    // blank Alerts screen. jsdom is one such environment, which is how
    // this was found.
    if (row instanceof HTMLElement && typeof row.scrollIntoView === 'function') {
      row.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [focusAlertId, focusFound]);

  /**
   * The ids the bulk-resolve button would actually send — ONE
   * expression, used for the count, the disabled state AND the
   * payload.
   *
   * These were three separate expressions: the label and the
   * `disabled` test both read `selected.size`, while the payload was
   * `rows.filter(...)`. `selected` is a set of ids the operator ticked
   * at some earlier render and nothing prunes it, so as soon as the
   * two disagree — rows refreshed underneath the selection, an alert
   * resolved by another operator, a mute sweeping its device's rows
   * closed — the button stayed enabled over a non-empty `selected`,
   * the filter produced `[]`, and the length guard returned without
   * sending anything. That is the same silent no-op the `Number(a.id)`
   * bug produced below: the operator presses Resolve, sees no error,
   * and nothing is resolved. Deriving once makes the state the button
   * shows and the state it acts on the same value by construction.
   */
  const needle = search.trim().toLowerCase();
  const shownRows = rows.filter(
    (a) =>
      alertMatchesKind(a.sourceKind, kindFilter) &&
      (!needle || whereOf(a).toLowerCase().includes(needle)),
  );
  // Only rows on screen: a filter must not leave a tick that a Resolve would
  // act on, out of sight.
  const selectedIds = shownRows.filter((a) => selected.has(a.id)).map((a) => a.id);

  /**
   * Drop ids that no longer match a visible row, so a selection cannot
   * accumulate ghosts across refreshes. Not what makes the button
   * correct — `selectedIds` above does that — but it keeps the
   * checkbox state honest and stops `selected` growing without bound
   * on a screen an operator leaves open for a shift. Guarded on the
   * size so it is a no-op (no re-render) in the common case.
   */
  useEffect(() => {
    setSelected((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(rows.map((a) => a.id));
      const next = new Set([...prev].filter((id) => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [rows]);

  if (!site) {
    return (
      <div className="nx-alerts">
        <h1 className="nx-section-title">Alerts</h1>
        <NoSiteNotice />
      </div>
    );
  }

  const allSelected = shownRows.length > 0 && shownRows.every((a) => selected.has(a.id));

  const toggleRow = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(shownRows.map((a) => a.id)));
  };

  const runBulkResolve = () => {
    // Alert ids are STRINGS and are sent through as-is. `alerts.alert_id`
    // is a `String(64)` primary key holding `alert_{uuid4hex}` — there
    // is no numeric key on that table. An earlier version of this
    // coerced with `Number(a.id)` and dropped everything that failed
    // `Number.isFinite`, which is EVERY real alert: the button would
    // have reported success having sent an empty batch.
    if (selectedIds.length === 0) return;
    setBulkOutcome(null);
    bulkResolve.mutate(selectedIds, {
      onSuccess: (result) => {
        setBulkOutcome(result);
        setSelected(new Set());
      },
    });
  };

  const openMuteDialog = (a: AlertRow) => {
    if (!a.sourceKind || !a.sourceId) return;
    setMuteReason('');
    setMuteTtl('24h');
    setMutePicking(false);
    setMuteTarget({ kind: a.sourceKind, id: a.sourceId });
  };

  const openMutePicker = () => {
    setMuteReason('');
    setMuteTtl('24h');
    setMutePicking(true);
    setMuteTarget({ kind: 'robot', id: '' });
  };

  const confirmMute = () => {
    if (!muteTarget?.id) return;
    const ttlMs = TTL_CHOICES.find((c) => c.value === muteTtl)?.ttlMs;
    setMuteOutcome(null);
    createMute.mutate(
      {
        source_kind: muteTarget.kind,
        source_id: muteTarget.id,
        ...(muteReason.trim() ? { reason: muteReason.trim() } : {}),
        ...(ttlMs !== undefined ? { ttl_ms: ttlMs } : {}),
      },
      {
        onSuccess: (result) => {
          setMuteOutcome({
            device: `${muteTarget.kind} ${muteTarget.id}`,
            resolved: result.resolved,
          });
          setMuteTarget(null);
        },
      },
    );
  };

  /** The row's "What to do" box: open or closed. */
  const boxOpen = (a: AlertRow) =>
    boxOverride.get(a.id) ?? (narrow ? alertOpensExpandedOnPhone(a) : alertOpensExpanded(a));
  const toggleBox = (a: AlertRow) => {
    setBoxOverride((prev) => new Map(prev).set(a.id, !boxOpen(a)));
  };
  const splitOf = (a: AlertRow) => splitAlertWords(wordsOf(a).message);
  const renderWhat = (a: AlertRow) => {
    const { what, more, toDo } = splitOf(a);
    return (
      <div className="nx-alerts__what" data-testid={`alerts-what-${a.id}`}>
        <strong>{what}</strong>
        {underBox.has(a.id) && (
          <span className="nx-alerts__more" data-testid={`alerts-under-box-${a.id}`}>
            {UNDER_BOX_WORDS}
          </span>
        )}
        {more && <span className="nx-alerts__more">{more}</span>}
        {toDo &&
          (boxOpen(a) ? (
            <div className="nx-alerts__todo" data-testid={`alerts-todo-${a.id}`}>
              <span className="nx-alerts__todo-label">What to do:</span> {toDo}
            </div>
          ) : (
            <button
              type="button"
              className="nx-alerts__todo-link"
              onClick={(e) => {
                e.stopPropagation();
                toggleBox(a);
              }}
              data-testid={`alerts-todo-open-${a.id}`}
            >
              What to do ›
            </button>
          ))}
      </div>
    );
  };
  const renderWhere = (a: AlertRow) => {
    // Rendered inline, so the words stay one sentence for a screen reader
    // and for anyone copying them; the device leads and the place is quiet.
    const text = whereOf(a);
    const sep = text.indexOf(' · ');
    return sep < 0 ? (
      <strong>{text}</strong>
    ) : (
      <>
        <strong>{text.slice(0, sep)}</strong>
        <span className="nx-alerts__where-rest">{text.slice(sep)}</span>
      </>
    );
  };
  const placeFocus = (a: AlertRow) => (a.id ? alertPlaceFocus(a) : null);
  const sinceClock = (a: AlertRow) =>
    audience === 'site' ? (wordsOf(a).headTime ?? '—') : formatDateTime(a.timestamp);
  const sinceAge = (a: AlertRow) => {
    const opened = alertOpenedMs(a);
    return opened === null ? null : `${alertAgeWords(wallNowMs - opened)} ago`;
  };
  const ackedName = (a: AlertRow): string => {
    if (!a.ackedBySub) return 'Not acked';
    const label = formatActor({ actor_sub: a.ackedBySub, actor_email: a.ackedByEmail ?? null });
    // A site reader gets a name only when there is one: the raw subject is
    // an id.
    return audience === 'site' && label === a.ackedBySub ? 'Acked' : label;
  };
  const renderActions = (a: AlertRow) => (
    <>
      {flags.alertAckEnabled && (
        <button
          type="button"
          className={`nx-screen-btn${a.ackedBySub ? '' : ' nx-screen-btn--primary'}`}
          disabled={Boolean(a.ackedBySub) || ack.isPending || actionsOff}
          onClick={() => ack.mutate(a.id)}
          data-testid={`alerts-ack-${a.id}`}
        >
          {a.ackedBySub ? 'Acked' : 'Ack'}
        </button>
      )}
      {flags.alertResolveEnabled && (
        <button
          type="button"
          className="nx-screen-btn"
          disabled={resolve.isPending || actionsOff}
          onClick={() => resolve.mutate(a.id)}
          data-testid={`alerts-resolve-${a.id}`}
        >
          Resolve
        </button>
      )}
      <RowMenu
        label="More for this alert"
        testId={`alerts-more-${a.id}`}
        items={[
          ...(canMute && a.sourceKind && a.sourceId && isMutableSourceKind(a.sourceKind)
            ? [
                {
                  label: 'Mute this device…',
                  disabled: createMute.isPending || actionsOff,
                  onClick: () => openMuteDialog(a),
                  testId: `alerts-mute-${a.id}`,
                },
              ]
            : []),
          {
            label: 'Report a problem about this',
            disabled: actionsOff,
            onClick: () => openReportProblem(reportPrefillFor(a, splitOf(a).what)),
            testId: `alerts-report-${a.id}`,
          },
        ]}
      />
    </>
  );

  const columns: DataTableColumn<AlertRow>[] = [
    {
      id: 'select',
      // No card label: a control that says what it is by being a checkbox.
      headerLabel: '',
      header: (
        <input
          type="checkbox"
          checked={allSelected}
          onChange={toggleAll}
          aria-label="Select all alerts"
          data-testid="alerts-select-all"
        />
      ),
      width: '4%',
      // The whole cell, not only the box, is kept from opening the row: a
      // press that just misses the box must not navigate away and drop the
      // selection.
      stopsRowClick: true,
      cell: (a) => (
        <input
          type="checkbox"
          checked={selected.has(a.id)}
          onChange={() => toggleRow(a.id)}
          aria-label={`Select alert ${a.id}`}
          data-testid={`alerts-select-${a.id}`}
        />
      ),
    },
    {
      id: 'severity',
      header: 'Severity',
      width: '14%',
      cell: (a) => {
        const words = alertRuleWords(a, audience);
        return (
          <span className="nx-status-cell">
            <StatusChip status={severityKind(a.severity)} label={SEVERITY_WORD[a.severity] ?? a.severity} />
            {words && <span className="nx-cell-small">{words}</span>}
            {/* The row's raw id is for ops only: a site audience never reads
                ids. It keeps its clicks, being there to be copied: a
                double-click to select it would first open the row. */}
            {audience === 'ops' && (
              <code
                className="nx-alerts__id"
                onClick={(e) => e.stopPropagation()}
                data-testid={`alerts-id-${a.id}`}
              >
                {a.id}
              </code>
            )}
          </span>
        );
      },
      sortable: true,
      sortValue: (a) => alertOrderKey(a, boxOf(a)),
    },
    {
      id: 'where',
      header: 'Where',
      width: '16%',
      // Straight after Severity, so a phone's card says what and where
      // before anything else. It replaced the Source column, which named
      // the same device without its kind or its floor.
      cell: (a) => {
        const focus = placeFocus(a);
        return (
          <>
            <span data-testid={`alerts-where-${a.id}`}>{renderWhere(a)}</span>
            {focus && (
              <span className="nx-alerts__show-where" onClick={(e) => e.stopPropagation()}>
                <ShowWhereLink focus={focus} />
              </span>
            )}
          </>
        );
      },
      sortable: true,
      sortValue: (a) => naturalKey(whereOf(a)),
    },
    {
      id: 'message',
      header: 'What',
      width: '30%',
      cell: renderWhat,
      sortable: true,
      sortValue: (a) => splitOf(a).what.toLowerCase(),
    },
    {
      id: 'time',
      header: 'Since',
      width: '12%',
      // A site reader reads the bell's own head time (`AlertWords.headTime`),
      // never a second reckoning of it: hotel words keep exactly the moment
      // their "Since" names, any other row its own time, on the site's
      // clock or labelled "(device time)". Ops keep the row's own time.
      cell: (a) => (
        <>
          <span data-testid={`alerts-since-${a.id}`}>{sinceClock(a)}</span>
          {sinceAge(a) && <span className="nx-cell-small">{sinceAge(a)}</span>}
        </>
      ),
      sortable: true,
      sortValue: (a) => alertOpenedMs(a),
    },
    {
      id: 'acked',
      header: 'Acked by',
      width: '12%',
      // Answers "has anyone got this one?". The acker's email is snapshotted
      // on the row at ack time; the raw subject is the honest fallback for
      // ops when the token carried no email claim, and the tooltip keeps the
      // subject reachable.
      cell: (a) => {
        if (!a.ackedBySub) return <span className="nx-cell-small">Not acked</span>;
        const who = { actor_sub: a.ackedBySub, actor_email: a.ackedByEmail ?? null };
        const when = a.ackedAtMs
          ? audience === 'site'
            ? dayClockText(a.ackedAtMs, clock, wallNowMs)
            : formatDateTime(a.ackedAtMs)
          : null;
        return (
          <span
            className="nx-alerts__acked"
            data-testid={`alerts-acked-${a.id}`}
            title={audience === 'ops' ? actorTitle(who) || undefined : undefined}
          >
            {ackedName(a)}
            {when && <span className="nx-cell-small">{when}</span>}
          </span>
        );
      },
      sortable: true,
      sortValue: (a) => (a.ackedBySub ? ackedName(a).toLowerCase() : ''),
    },
    {
      id: 'actions',
      headerLabel: '',
      header: '',
      width: '12%',
      // The whole cell keeps its clicks: the row under it opens the device,
      // and an Ack must not also navigate away from the alert it acked.
      stopsRowClick: true,
      cell: (a) => <div className="nx-alerts__actions">{renderActions(a)}</div>,
    },
  ];

  /**
   * Which rows open anything. A click opens what the bell's item for the
   * same alert opens (`handleAlertAction`): for a site audience the
   * Overview pointed at the place, on every row, because the Overview
   * itself says when an alert is about no one place; for ops the device's
   * own screen and panel. An ops row about no one place (the site box, a
   * door adapter, lift control, the fire alarm) has no such screen, and
   * the bell sends it to `/alerts` -- this screen, so a click would only
   * reload it and drop a `?alert=` the reader arrived with. Such a row is
   * not offered as a link at all.
   *
   * NOR IS ONE WHOSE DRAWER IS NOT THERE TO OPEN. Fleet and Tasks open a
   * drawer only for a robot or a job in their own lists, and nothing
   * otherwise: a failed job that has aged into Older tasks, or a robot
   * renamed since its alert opened, would land on the right screen with
   * nothing opened (`useAlertLinkOpens`). The row is a link again the
   * moment its target is listed.
   */
  const rowOpens = (a: AlertRow): boolean => {
    if (audience === 'site') return Boolean(a.id);
    const target = navigationTargetFor(a);
    if (target.path === '/alerts') return false;
    return target.deepLink === null || linkOpens(target.deepLink);
  };

  const muteRows = mutes.data ?? [];

  const muteColumns: DataTableColumn<AlertMuteOut>[] = [
    {
      id: 'device',
      header: 'Device',
      width: '32%',
      // Raw kind and id: only ops read this list (A18).
      cell: (m) => (
        <span>
          {m.source_kind} <code>{m.source_id}</code>
        </span>
      ),
      sortable: true,
      sortValue: (m) => naturalKey(`${m.source_kind} ${m.source_id}`),
    },
    {
      id: 'reason',
      header: 'Reason',
      width: '28%',
      cell: (m) => m.reason || '—',
      sortable: true,
      sortValue: (m) => (m.reason ?? '').toLowerCase(),
    },
    {
      id: 'created',
      header: 'Muted',
      width: '15%',
      cell: (m) => formatDateTime(m.created_at_ms),
      sortable: true,
      sortValue: (m) => m.created_at_ms,
    },
    {
      id: 'expires',
      header: 'Expires',
      width: '15%',
      cell: (m) => (m.expires_at_ms ? formatDateTime(m.expires_at_ms) : 'Until unmuted'),
      sortable: true,
      sortValue: (m) => m.expires_at_ms ?? Number.MAX_SAFE_INTEGER,
    },
    {
      id: 'actions',
      headerLabel: '',
      header: '',
      width: '10%',
      cell: (m) => (
        <button
          type="button"
          className="nx-screen-btn"
          disabled={deleteMute.isPending || actionsOff}
          onClick={() => deleteMute.mutate(m.id)}
          data-testid={`alerts-unmute-${m.id}`}
        >
          Unmute
        </button>
      ),
    },
  ];

  // Open alerts only: a mute is ops' (A18), so its count is too.
  const summary = alertSummary(rows, canMute ? muteRows.length : null, wallNowMs);

  const actionError = ack.error ?? resolve.error ?? bulkResolve.error ?? createMute.error ?? deleteMute.error;

  return (
    <div className="nx-alerts">
      <ScreenTitleRow title="Alerts">
        <span className="nx-alerts__sound" data-testid="alerts-sound">
          <span className="nx-alerts__sound-label">Sound on this computer</span>
          <StatusChip status={soundMuted ? 'warning' : 'active'} label={soundMuted ? 'Off' : 'On'} />
        </span>
        {canMute && (
          <button
            type="button"
            className="nx-screen-btn"
            disabled={actionsOff}
            onClick={openMutePicker}
            data-testid="alerts-mute-a-device"
          >
            Mute a device…
          </button>
        )}
      </ScreenTitleRow>
      <HelpScreenNote screen="alerts" />

      <SummaryLine
        headline={summary.headline}
        facts={summary.facts}
        label="Alerts summary"
        testId="alerts-summary"
      />

      {/* The errors' own text carries an HTTP status and the alert's id:
          ops read it as before; a site reader reads plain words. */}
      {actionError != null && (
        <AlertBanner variant="error" title="Action failed">
          {audience === 'site'
            ? SITE_ACTION_ERROR_TEXT
            : actionError instanceof Error
              ? actionError.message
              : 'Unknown error'}
        </AlertBanner>
      )}

      {alerts.isError && audience === 'site' && (
        <AlertBanner variant="error" title={SITE_LOAD_ERROR_TITLE}>
          {SITE_LOAD_ERROR_TEXT}
          {/* TanStack keeps the last good list beside the error: say so,
              rather than let it pass for the current one. */}
          {rows.length > 0 ? ` ${SITE_LOAD_ERROR_STALE}` : ''}
        </AlertBanner>
      )}
      {alerts.isError && audience === 'ops' && (
        <AlertBanner variant="error" title="Failed to load alerts">
          {alerts.error instanceof Error ? alerts.error.message : 'Unknown error'}
        </AlertBanner>
      )}

      {bulkOutcome && bulkOutcome.skipped.length > 0 && (
        <AlertBanner variant="warning" title="Some alerts were not resolved">
          Resolved {bulkOutcome.resolved}; skipped {bulkOutcome.skipped.length}:
          <ul className="nx-alerts__skipped">
            {bulkOutcome.skipped.map((s) => (
              <li key={s.id}>
                <code>{s.id}</code> — {s.reason}
              </li>
            ))}
          </ul>
        </AlertBanner>
      )}

      {canMute && muteOutcome && (
        <AlertBanner variant="info" title={`Muted ${muteOutcome.device}`}>
          {muteOutcome.resolved > 0
            ? `${muteOutcome.resolved} open alert${muteOutcome.resolved === 1 ? '' : 's'} on this device ${muteOutcome.resolved === 1 ? 'was' : 'were'} resolved.`
            : 'No new alerts will open for this device while the mute is active.'}
        </AlertBanner>
      )}

      {/* Mutes are ops' (A18), so a site reader has one list and no tabs. */}
      {canMute && (
        <ScreenTabs<AlertsTab>
          label="Alert lists"
          testIdPrefix="alerts-tab"
          active={tab}
          onChange={setTab}
          tabs={[
            { id: 'open', label: 'Open', count: rows.length },
            { id: 'muted', label: 'Muted devices', count: muteRows.length },
          ]}
        />
      )}

      {tab === 'open' &&
        (alerts.isLoading ? (
          <SkeletonLoader shape="table-row" count={3} />
        ) : rows.length === 0 ? (
          // A list that could not be read is not an empty one: no "healthy"
          // under the banner that says it could not be read (the state is
          // unknown until it loads, as `help.alerts.load_failed` says). For
          // EVERY reader (A17c): ops read the same banner, and "healthy"
          // under it was the same false claim.
          alerts.isError ? null : (
            <EmptyState
              title="No open alerts"
              description="Robots, doors, lifts and gateways are healthy — or their alerts have been resolved or muted."
            />
          )
        ) : (
          <>
            <TableToolbar search={search} onSearch={setSearch} searchLabel="Search by device">
              <div className="nx-alerts__kinds" role="group" aria-label="Filter by kind">
                {KIND_FILTERS.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    aria-pressed={kindFilter === f.id}
                    className={`nx-alerts__kind${kindFilter === f.id ? ' nx-alerts__kind--active' : ''}`}
                    onClick={() => setKindFilter(f.id)}
                    data-testid={`alerts-kind-${f.id}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              {/* No tick boxes on a phone's cards, so nothing to act on. */}
              {!narrow && (
                <div role="toolbar" aria-label="Bulk alert actions">
                  <button
                    type="button"
                    className="nx-screen-btn"
                    disabled={selectedIds.length === 0 || bulkResolve.isPending || actionsOff}
                    onClick={runBulkResolve}
                    data-testid="alerts-bulk-resolve"
                  >
                    Resolve {selectedIds.length} selected
                  </button>
                </div>
              )}
            </TableToolbar>
            <div ref={tableRef}>
              <DataTable
                fixedLayout
                columns={columns}
                rows={shownRows}
                getRowId={(a) => a.id}
                ariaLabel="Alerts"
                highlightRowId={focusFound ? (focusAlertId as string) : undefined}
                onRowClick={handleAlertAction}
                isRowClickable={rowOpens}
                defaultSort={SEVERITY_SORT}
                rowTone={(a) => (a.severity === 'critical' ? 'critical' : null)}
                emptyState={
                  <EmptyState
                    title="Nothing matches"
                    description="Clear the search or choose All kinds to see every open alert."
                  />
                }
                phone={{
                  rowHoldsControls: true,
                  sortKeys: [
                    { id: 'severity', label: 'Severity' },
                    { id: 'time', label: 'Newest' },
                    { id: 'where', label: 'Where' },
                  ],
                  renderRow: (a) => {
                    const { what, more, toDo } = splitOf(a);
                    const open = boxOpen(a);
                    const focus = placeFocus(a);
                    return (
                      <>
                        <span className="nx-phone-row__head">
                          <StatusChip
                            status={severityKind(a.severity)}
                            label={SEVERITY_WORD[a.severity] ?? a.severity}
                          />
                          {sinceAge(a) && <span className="nx-cell-small">{sinceAge(a)}</span>}
                        </span>
                        <strong className="nx-phone-row__name">{what}</strong>
                        {underBox.has(a.id) && <span className="nx-cell-small">{UNDER_BOX_WORDS}</span>}
                        <span className="nx-cell-small" data-testid={`alerts-where-${a.id}`}>
                          {whereOf(a)}
                        </span>
                        {open && more && <span className="nx-alerts__more">{more}</span>}
                        {open && toDo && (
                          <div className="nx-alerts__todo">
                            <span className="nx-alerts__todo-label">What to do:</span> {toDo}
                          </div>
                        )}
                        {open && <div className="nx-alerts__phone-actions">{renderActions(a)}</div>}
                        <span className="nx-alerts__phone-links">
                          <button
                            type="button"
                            className="nx-alerts__todo-link"
                            onClick={() => toggleBox(a)}
                          >
                            {open ? 'Less' : 'More ›'}
                          </button>
                          {focus && <ShowWhereLink focus={focus} />}
                        </span>
                      </>
                    );
                  },
                }}
              />
            </div>
          </>
        ))}

      {canMute && tab === 'muted' &&
        (muteRows.length === 0 ? (
          <EmptyState
            title="No muted devices"
            description="Muting a device stops new alerts from opening for it (and resolves its open ones) until the mute expires or is removed."
          />
        ) : (
          <DataTable
            fixedLayout
            columns={muteColumns}
            rows={muteRows}
            getRowId={(m) => String(m.id)}
            ariaLabel="Muted devices"
            defaultSort={DEVICE_SORT}
          />
        ))}

      {canMute && muteTarget && (
        <FormDialog
          title={mutePicking ? 'Mute a device' : `Mute ${muteTarget.kind} ${muteTarget.id}`}
          confirmLabel={createMute.isPending ? 'Muting…' : 'Mute'}
          variant="warning"
          confirmDisabled={createMute.isPending || !muteTarget.id}
          onConfirm={confirmMute}
          onCancel={() => setMuteTarget(null)}
          testId="alert-mute-dialog"
          cancelTestId="alert-mute-cancel"
          confirmTestId="alert-mute-confirm"
        >
          <p className="nx-alerts__mute-hint">{MUTE_DIALOG_HINT}</p>
          {mutePicking && (
            <>
              <label className="nx-alerts__mute-field">
                <span>Kind</span>
                <select
                  value={muteTarget.kind}
                  onChange={(e) => setMuteTarget({ kind: e.target.value, id: '' })}
                  data-testid="alert-mute-kind"
                >
                  {MUTE_PICK_KINDS.map((k) => (
                    <option key={k.value} value={k.value}>
                      {k.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="nx-alerts__mute-field">
                <span>Device</span>
                <select
                  value={muteTarget.id}
                  onChange={(e) => setMuteTarget({ kind: muteTarget.kind, id: e.target.value })}
                  data-testid="alert-mute-device"
                >
                  <option value="">Choose…</option>
                  {pickableDevices(muteTarget.kind).map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </label>
            </>
          )}
          <label className="nx-alerts__mute-field">
            <span>Reason</span>
            <input
              type="text"
              value={muteReason}
              onChange={(e) => setMuteReason(e.target.value)}
              placeholder="e.g. under test until vendor fix"
              data-testid="alert-mute-reason"
            />
          </label>
          <label className="nx-alerts__mute-field">
            <span>Duration</span>
            <select
              value={muteTtl}
              onChange={(e) => setMuteTtl(e.target.value as TtlChoice)}
              data-testid="alert-mute-ttl"
            >
              {TTL_CHOICES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
        </FormDialog>
      )}
    </div>
  );
}
